import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { AIPrepLogoIcon } from "../components/CustomIcons";
import trappingRainWater from "../assets/landing/trapping-rain-water-animation.png";
import reviewScreenshot from "../assets/landing/review.png";
import aiPartnerScreenshot from "../assets/landing/ai-partner-screenshot.png";
import "../styles/landing.css";

const heroQuotes = [
  {
    icon: "*",
    text: "Every interview cycle feels like starting from zero.",
  },
  {
    icon: "?",
    text: "I understand the problem now - but a few weeks later, it's gone.",
  },
  {
    icon: ">",
    text: "Some explanations are too abstract. Visualization would help a lot.",
  },
  {
    icon: "=",
    text: "I track everything, but my notes feel fragile and hard to reuse.",
  },
  {
    icon: "+",
    text: "Problems on LeetCode, explanations on Medium, videos on YouTube - all scattered.",
  },
  {
    icon: "!",
    text: "I can code it, but I struggle to explain my thinking under pressure.",
  },
  {
    icon: "#",
    text: "My Meta interview is in two weeks - I don't know what to focus on.",
  },
];

interface LandingPageProps {
  onEnterApp: () => void;
}

export const LandingPage: React.FC<LandingPageProps> = ({ onEnterApp }) => {
  const navigate = useNavigate();
  const [activeQuote, setActiveQuote] = React.useState(0);

  React.useEffect(() => {
    const intervalId = window.setInterval(() => {
      setActiveQuote((prev) => (prev + 1) % heroQuotes.length);
    }, 3000);

    return () => window.clearInterval(intervalId);
  }, []);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -60px 0px" }
    );

    document.querySelectorAll(".landing-fade").forEach((element) => {
      observer.observe(element);
    });

    return () => observer.disconnect();
  }, []);

  const handleEnterApp = React.useCallback(() => {
    onEnterApp();
    navigate("/", { replace: true });
  }, [navigate, onEnterApp]);

  const handleScrollToFeatures = React.useCallback(() => {
    const section = document.getElementById("landing-features");
    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  return (
    <div className="landing-page">
      <header className="landing-header">
        <nav className="landing-nav">
          <button type="button" className="landing-logo" onClick={handleEnterApp}>
            <span className="landing-logo-icon">
              <AIPrepLogoIcon size={24} color="white" />
            </span>
            AIPrep
          </button>
          <div className="landing-nav-links">
            <button type="button" className="landing-link" onClick={handleScrollToFeatures}>
              See How AIPrep Works
            </button>
            <button type="button" className="landing-btn landing-btn-primary" onClick={handleEnterApp}>
              Start Now - Free
            </button>
          </div>
        </nav>
      </header>

      <main>
        <section className="landing-hero">
          <div className="landing-hero-content landing-fade">
            <h1>
              Interview prep feels harder than it should.
              <br />
              <span>It&apos;s not you - it&apos;s the system.</span>
            </h1>
            <div className="landing-hero-quotes">
              {heroQuotes.map((quote, index) => (
                <div
                  key={quote.text}
                  className={`landing-hero-quote ${index === activeQuote ? "active" : ""}`}
                >
                  <span className="landing-hero-quote-icon" aria-hidden="true">
                    {quote.icon}
                  </span>
                  <span>{quote.text}</span>
                </div>
              ))}
            </div>
            <p className="landing-hero-bridge">You don&apos;t need more problems. You need a system that makes learning stick.</p>
            <div className="landing-hero-cta">
              <button type="button" className="landing-btn landing-btn-primary landing-btn-lg" onClick={handleEnterApp}>
                Start Now - Free
              </button>
              <button type="button" className="landing-btn landing-btn-secondary" onClick={handleScrollToFeatures}>
                See How AIPrep Works -&gt;
              </button>
            </div>
          </div>
        </section>

        <section id="landing-features" className="landing-section">
          <div className="landing-container landing-fade">
            <div className="landing-features-grid">
              <div className="landing-feature-content">
                <span className="landing-section-label">Problem-Centric Learning</span>
                <h3>Everything about a problem - in one place</h3>
                <p>
                  Everything related to a problem lives together.
                  <br />
                  No website switching. No scattered notes. No lost context.
                </p>
                    <p className="landing-feature-emphasis">For each problem, you get a single, permanent home for:</p>
                    <ul className="landing-feature-list">
                      <li>The problem statement and constraints</li>
                      <li>All standard solution code - from brute force to optimal</li>
                  <li>Clear explanations and trade-offs for each approach</li>
                  <li>Your notes, code, and diagrams - saved forever</li>
                  <li>Every AI conversation - permanently attached to the problem</li>
                </ul>
              </div>
              <div className="landing-feature-image">
                <div className="app-mock-container">
                  <div className="app-mock-main">
                    <div className="problem-header-bar">
                      <div className="problem-title-area">
                        <h4>14. Trapping Rain Water</h4>
                        <span className="problem-subtitle">(LeetCode 42)</span>
                      </div>
                      <div className="problem-icons">
                        <span className="icon-btn ai-btn" aria-hidden="true">
                          AI
                        </span>
                        <span className="icon-btn checkbox-btn" aria-hidden="true">
                          [ ]
                        </span>
                        <span className="icon-btn tag-btn" aria-hidden="true">
                          #
                        </span>
                      </div>
                    </div>

                    <div className="problem-tags-row">
                      <span className="difficulty-badge hard">Hard</span>
                      <span className="algo-tag">array</span>
                      <span className="algo-tag">two-pointers</span>
                      <span className="algo-tag">stack</span>
                      <span className="company-stat">Amazon:96%</span>
                      <span className="company-stat">Google:92%</span>
                    </div>

                    <div className="workbench-panel">
                      <div className="workbench-header-row">
                        <span className="workbench-title-icon">Practice Workbench</span>
                        <button type="button" className="expand-circle" aria-label="Collapse workbench">
                          ^
                        </button>
                      </div>
                      <div className="workbench-tabs-row">
                        <label className="tab active">My Algorithm</label>
                        <label className="tab">My Code</label>
                        <label className="tab">Notes</label>
                        <label className="tab">Drawing</label>
                      </div>
                      <div className="algorithm-input-area">
                        <div className="input-header">
                          <span>Algorithm Description</span>
                          <span className="input-icons">save expand</span>
                        </div>
                        <div className="input-placeholder">Describe your algorithm approach here...</div>
                      </div>
                      <button type="button" className="verify-algorithm-btn">
                        Verify My Algorithm
                      </button>
                    </div>

                    <div className="section-buttons-row">
                      <button type="button" className="section-btn section-btn-blue">
                        <span className="section-btn-icon">DOC</span>
                        Problem Description
                        <span className="section-btn-arrow">v</span>
                      </button>
                      <button type="button" className="section-btn section-btn-gray">
                        <span className="section-btn-icon">OK</span>
                        Detailed Algorithm
                        <span className="section-btn-arrow">v</span>
                      </button>
                      <button type="button" className="section-btn section-btn-teal">
                        <span className="section-btn-icon">&lt;/&gt;</span>
                        Solution Code
                        <span className="section-btn-arrow">v</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="landing-section landing-section-alt">
          <div className="landing-container landing-fade">
            <div className="landing-features-grid landing-features-grid-reverse landing-features-grid-center">
              <div className="landing-feature-content">
                <span className="landing-section-label">Deep Understanding</span>
                <h3>See the algorithm think - not just the final answer</h3>
                <p>
                  Hard problems aren&apos;t hard because the logic is complex. They&apos;re hard because the important
                  state changes are invisible. AIPrep makes those changes visible - step by step - so you can see
                  why decisions are made and how the solution unfolds.
                </p>
                <ul className="landing-feature-list">
                  <li>Watch the algorithm evolve, one step at a time</li>
                  <li>See key state changes live (stacks, pointers, DP, boundaries)</li>
                  <li>Understand why elements are added, removed, or updated</li>
                  <li>Ask AI questions at any step - with full context preserved</li>
                </ul>
                <p className="landing-feature-callout">When you can see the state change, the logic clicks.</p>
              </div>
              <div className="landing-feature-image">
                <img
                  src={trappingRainWater}
                  alt="Trapping Rain Water - Monotonic Stack Animation"
                  className="landing-image"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="landing-section landing-section-alt">
          <div className="landing-container landing-fade">
            <div className="landing-features-grid">
              <div className="landing-feature-content">
                <span className="landing-section-label">Daily Reviews</span>
                <h3>Never forget a solution you&apos;ve already learned</h3>
                <p>
                  You solved it once. You understood it. But a few weeks later, it&apos;s gone. AIPrep uses
                  scientifically-proven spaced repetition to keep solutions fresh in your memory - and an AI
                  assistant takes care of the rest: scheduling, selecting, and testing your recall.
                </p>
                <ul className="landing-feature-list">
                  <li>AI identifies which problems are slipping from memory and surfaces them at the right time</li>
                  <li>Problems you struggle with get more attention - automatically</li>
                  <li>Optimal intervals between reviews maximize long-term retention</li>
                  <li>Set your interview date and AI ensures you&apos;re ready when it matters</li>
                  <li>No spreadsheets, no manual tracking - just show up and review</li>
                </ul>
                <p className="landing-feature-callout">Build understanding once. Keep it forever.</p>
              </div>
              <div className="landing-feature-image">
                <img
                  src={reviewScreenshot}
                  alt="Daily Reviews - AI-powered spaced repetition"
                  className="landing-image landing-image-top"
                />
              </div>
            </div>
          </div>
        </section>

        <section id="landing-how-it-works" className="landing-section">
          <div className="landing-container landing-fade">
            <div className="landing-features-grid landing-features-grid-reverse">
              <div className="landing-feature-content">
                <span className="landing-section-label">AI Partner</span>
                <h3>Your AI partner - embedded in the problem</h3>
                <p className="landing-feature-emphasis">Solve the problem your way - then verify it.</p>
                <p>
                  AIPrep&apos;s AI is loaded with the full problem, all approaches, and your current state, so you can
                  validate solutions, compare algorithms, and explore alternatives without starting over.
                </p>
                <ul className="landing-feature-list">
                  <li>Verify your code and algorithm for correctness and complexity</li>
                  <li>Ask why one approach works better than another</li>
                  <li>Compare brute force, optimized, and alternative solutions</li>
                  <li>Debug edge cases and assumptions with AI guidance</li>
                  <li>Get clear explanations from AI whenever something doesn&apos;t fully make sense</li>
                  <li>Chat freely about the problem, algorithms, and code - every AI conversation is saved</li>
                </ul>
                <p className="landing-feature-callout">The context never resets. Your understanding compounds.</p>
              </div>
              <div className="landing-feature-image landing-feature-image-ai">
                <img
                  src={aiPartnerScreenshot}
                  alt="AI Partner - Embedded in the Problem"
                  className="landing-image landing-image-ai"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="landing-cta-section">
          <div className="landing-container landing-fade">
            <h2>Your prep shouldn&apos;t reset every year</h2>
            <p>Each interview cycle gets easier - not harder. Build understanding once. Keep it forever.</p>
            <button type="button" className="landing-btn landing-btn-secondary landing-btn-lg" onClick={handleEnterApp}>
              Start Preparing Free
            </button>
          </div>
        </section>
      </main>

      <footer className="landing-footer">
        <div className="landing-footer-content">
          <button type="button" className="landing-logo landing-footer-logo" onClick={handleEnterApp}>
            <span className="landing-logo-icon">
              <AIPrepLogoIcon size={22} color="white" />
            </span>
            AIPrep
          </button>
          <div className="landing-footer-links">
            <a href="mailto:support@aiprep.io">
              Feedback
            </a>
            <Link to="/privacy">
              Privacy
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};
